import { getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { updateNodeProperty } from "../../../store/slices/BuilderSlice/builderSlice";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import { getValueUnit } from "../editUtils";
import "./style.scss";
import OliverText from "@builder/components/general/OliverText";
import Button from "@builder/components/general/Button";
import ColorInput from "@builder/components/general/ColorInput";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { EditableProps } from "@builder/components/editable/editableProps";
import { getStyle, getStylePath } from "@builder/components/editable/helpers";

type ShadowCSS = {
    x: string;
    y: string;
    blur: string;
    spread: string;
    color: string;
};

const AddShadows = ({ path, stateMode }: EditableProps) => {
    const dispatch = useAppDispatch();
    const shadowRef = useRef<HTMLDivElement>(null);
    // const styles = useAppSelector(getSelectedNodeStyle);
    const { t } = useTranslation();
    const node = useAppSelector(getSelectedNode);

    if (!node) return null;

    const styles = getStyle(node, path, stateMode);
    const stylePath = getStylePath(path, stateMode);

    if (!styles) return null;
    const shadowArrayToString = (shadows: ShadowCSS[]): string => {
        let shadowString = "";
        shadows.forEach((shadow, index) => {
            shadowString += `${shadow.x}px ${shadow.y}px ${shadow.blur}px ${shadow.spread}px ${shadow.color}`;
            if (index !== shadows.length - 1) shadowString += ", ";
        });
        return shadowString;
    };

    const getShadows = (shadowString: string = ""): ShadowCSS[] => {
        const shadowSplit = shadowString.split(", ");
        const shadows: ShadowCSS[] = [];
        if (shadowSplit.length === 1 && !shadowSplit[0]) {
            return shadows;
        } else {
            for (let i = 0; i < shadowSplit.length; i++) {
                const shadowPlaceholder: ShadowCSS = {
                    x: "0",
                    y: "0",
                    blur: "0",
                    spread: "0",
                    color: "transparent"
                };
                shadowSplit[i].split(" ").map((value, index) => {
                    switch (index) {
                        case 0:
                            shadowPlaceholder.x = getValueUnit(value).value;
                            break;
                        case 1:
                            shadowPlaceholder.y = getValueUnit(value).value;
                            break;
                        case 2:
                            shadowPlaceholder.blur = getValueUnit(value).value;
                            break;
                        case 3:
                            shadowPlaceholder.spread = getValueUnit(value).value;
                            break;
                        case 4:
                            shadowPlaceholder.color = value;
                    }
                });
                shadows.push(shadowPlaceholder);
            }
            return shadows;
        }
    };

    const updateShadow = (index: number, type: keyof ShadowCSS, value: number | string): void => {
        (shadowArray[index][type] as string | number) = value;
        dispatch(updateNodeProperty({ propertyPath: [...stylePath, "boxShadow"], value: shadowArrayToString(shadowArray) }));
    };

    const shadowString = styles.stateStyle?.boxShadow || styles.default?.boxShadow;
    const shadowArray: ShadowCSS[] = getShadows(shadowString);

    const handleNumberInput = (e: React.FormEvent<HTMLInputElement>): void => {
        const currentShadow = parseInt((e.currentTarget.parentNode?.parentNode as HTMLElement).getAttribute("data-index") as string);
        const currentType = e.currentTarget.getAttribute("data-type");
        if (typeof currentShadow === "number" && typeof currentType === "string") {
            const currentString = e.currentTarget.value ? parseInt(e.currentTarget.value).toString() : "0";
            if (currentString.length === 0) {
                updateShadow(currentShadow, currentType as keyof ShadowCSS, 0);
            } else {
                const newChar = currentString.charAt(currentString.length - 1);
                if (/\d/.test(newChar)) {
                    updateShadow(currentShadow, currentType as keyof ShadowCSS, currentString);
                }
            }
        }
    };

    const handleColorInput = (value: string) => {
        const currentShadowElem = shadowRef.current;
        if (currentShadowElem) {
            const currentShadow = parseInt(currentShadowElem.getAttribute("data-index") as string);
            if (typeof currentShadow === "number") {
                updateShadow(currentShadow, "color" as keyof ShadowCSS, value);
            }
        }
    };

    const addNewShadow = () => {
        if (shadowString) {
            dispatch(updateNodeProperty({ propertyPath: [...stylePath, "boxShadow"], value: shadowString + ", 0px 0px 0px 0px #000000" }));
        } else {
            dispatch(updateNodeProperty({ propertyPath: [...stylePath, "boxShadow"], value: shadowString + "0px 0px 0px 0px #000000" }));
        }
    };

    const clearShadows = () => {
        dispatch(updateNodeProperty({ propertyPath: ["attr", "style", "boxShadow"], value: "" }));
    };

    const removeShadow = (index: number) => {
        const remaining = shadowArray.filter((_, i) => i !== index);
        dispatch(updateNodeProperty({ propertyPath: ["attr", "style", "boxShadow"], value: shadowArrayToString(remaining) }));
    };

    return (
        <div className="shadows-wrapper">
            <div className="color-input-wrapper">
                <div className="color-input-row">
                    <div className="left">
                        <OliverText text="Shadows" size="xs" translatePrefix="generalComponents.addShadows." />
                    </div>
                    <div className="right">
                        <Button
                            buttonStyle="grey"
                            size="fit"
                            onClick={addNewShadow}
                            icon={{ src: "/add.svg" }}
                            aria-label={t("generalComponents.addShadows.button")}
                        />
                    </div>
                </div>
            </div>
            <div className="shadow-list">
                {shadowArray.map((value, index) => (
                    <div className="shadow-input" key={index} data-index={index} ref={shadowRef}>
                        <Button
                            buttonStyle="transparent"
                            size="fit-small"
                            className="delete"
                            onClick={() => removeShadow(index)}
                            icon={{ src: "/cross.svg" }}
                        />
                        <label className="color-label" style={{ backgroundColor: value.color }}>
                            <ColorInput color={value.color} callback={handleColorInput} options={{ noText: true }} />
                        </label>
                        <label className="other-label">
                            <input type="text" data-type="x" value={value.x} onInput={handleNumberInput} />X
                        </label>
                        <label className="other-label">
                            <input type="text" data-type="y" value={value.y} onInput={handleNumberInput} />Y
                        </label>
                        <label className="other-label">
                            <input type="text" data-type="blur" value={value.blur} onInput={handleNumberInput} />
                            {t("generalComponents.addShadows.Blur")}
                        </label>
                        <label className="other-label">
                            <input type="text" data-type="spread" value={value.spread} onInput={handleNumberInput} />
                            {t("generalComponents.addShadows.Spread")}
                        </label>
                    </div>
                ))}
            </div>
            {shadowArray.length > 0 && (
                <button className="clear" onClick={clearShadows}>
                    {t("generalComponents.addShadows.Clear all shadows")}
                </button>
            )}
        </div>
    );
};

export default AddShadows;
